import { getCustomRoles } from './useCustomRoles';
import { useSelector } from 'react-redux';
import { selectCustomRoles } from '../redux/slices/customRolesSlice';

// Helper function to normalize email addresses
const normalizeEmail = (email) => {
    if (!email) return '';
    return email.trim().toLowerCase();
};

// Non-hook version for use in regular functions
export const mapUserRoles = (userRoles, userEmail, state) => {
    // Create a copy of userRoles to avoid mutating the original
    const allRoles = [...userRoles];
    
    // Get custom role from the Redux state if provided, otherwise use the standalone function
    if (userEmail) {
        const normalizedEmail = normalizeEmail(userEmail);
        
        // Check for custom roles with normalized email
        let customRole = null;
        
        if (state && state.customRoles?.roles) {
            // Try to find the role with exact match first
            customRole = state.customRoles.roles[normalizedEmail];
            
            // If not found, try a case-insensitive search
            if (!customRole) {
                const emails = Object.keys(state.customRoles.roles);
                const matchingEmail = emails.find(email => 
                    normalizeEmail(email) === normalizedEmail);
                
                if (matchingEmail) {
                    customRole = state.customRoles.roles[matchingEmail];
                }
            }
        } else {
            customRole = getCustomRoles(normalizedEmail);
        }

        // Add the custom role to user roles if one exists and isn't already included
        if (customRole && !allRoles.includes(customRole)) {
            allRoles.push(customRole); // Add custom role if not already present
        }
    }

    return {
        Admin: allRoles.includes('Admin'),
        Apps: allRoles.includes('Apps'),
        AppsManager: allRoles.includes('AppsManager'),
        ControlEngineer: allRoles.includes('ControlEngineer'),
        ElectricalEngineer: allRoles.includes('ElectricalEngineer'),
        MechanicalEngineer: allRoles.includes('MechanicalEngineer'),
        Engineer: allRoles.includes('Engineer'),
        TestPit: allRoles.includes('TestPit'),
        PSN: allRoles.includes('PSN'),
        Sales: allRoles.includes('Sales'),
        ReadOnly: allRoles.includes('ReadOnly'),
        Scheduling: allRoles.includes('Scheduling'),
        EngineerManager: allRoles.includes('EngineerManager'),
        Accounting: allRoles.includes('Accounting'),
        NorthPrairie: allRoles.includes('NorthPrairie'),
        AssemblySchedule: allRoles.includes('AssemblySchedule'),
    };
};

// Hook version for use in React components
export const useRoleMapping = (userRoles, userEmail) => {
    // Get custom roles directly from Redux store
    const customRoles = useSelector(selectCustomRoles);
    
    // Create a copy of userRoles to avoid mutating the original
    const allRoles = [...userRoles];
    
    // Only proceed if we have a userEmail
    if (userEmail) {
        const normalizedEmail = normalizeEmail(userEmail);
        let customRole = null;
        
        // Try direct lookup first
        if (customRoles[normalizedEmail]) {
            customRole = customRoles[normalizedEmail];
        } else {
            // Try case-insensitive search as fallback
            const emails = Object.keys(customRoles);
            const matchingEmail = emails.find(email => 
                normalizeEmail(email) === normalizedEmail);
            
            if (matchingEmail) {
                customRole = customRoles[matchingEmail];
            }
        }
        
        // Add custom role if found and not already in allRoles
        if (customRole && !allRoles.includes(customRole)) {
            allRoles.push(customRole);
        }
    }
    
    return {
        Admin: allRoles.includes('Admin'),
        Apps: allRoles.includes('Apps'),
        AppsManager: allRoles.includes('AppsManager'),
        ControlEngineer: allRoles.includes('ControlEngineer'),
        ElectricalEngineer: allRoles.includes('ElectricalEngineer'),
        MechanicalEngineer: allRoles.includes('MechanicalEngineer'),
        Engineer: allRoles.includes('Engineer'),
        TestPit: allRoles.includes('TestPit'),
        PSN: allRoles.includes('PSN'),
        Sales: allRoles.includes('Sales'),
        ReadOnly: allRoles.includes('ReadOnly'),
        Scheduling: allRoles.includes('Scheduling'),
        EngineerManager: allRoles.includes('EngineerManager'),
        Accounting: allRoles.includes('Accounting'),
        NorthPrairie: allRoles.includes('NorthPrairie'),
        AssemblySchedule: allRoles.includes('AssemblySchedule'),
    };
};
